import * as swcHelpers from '@swc/helpers';
import * as React from 'react';
import { PaymentTypes } from '@grandvisionhq/api-client';
import { useAccount } from '@grandvisionhq/auth';
import { Context } from '@grandvisionhq/carts-v2';
import { Util, toast } from '@grandvisionhq/common';
import { useLink, useLocation } from '@grandvisionhq/frk-routing';
import { useCartStorage } from '@grandvisionhq/lib-utils-cart';
import { useSync } from '@grandvisionhq/lib-utils-w3c';
import { WithUserData } from '@grandvisionhq/my-account';
import { Hooks, usePaymentsUIContext, PaymentMethod, PaymentFlow, PaypalPaymentEventsProvider } from '@grandvisionhq/payments';
import { useDispatch, useSelector } from '@grandvisionhq/state';
import { setDonation } from '../actions.js';
import { useCheckoutSettings } from '../context/CheckoutSettings/context.js';
import { pushDonationSelectEventToDataLayer } from '../data-layer.js';
import '@grandvisionhq/utils';
import '../StepShipping/selectors.js';
import 'lodash/isEmpty';
import { ApplePaySessionErrors, CreateOrderErrors } from '../mappers/errors-mapping.js';
import '../utils/get-customer-details-payload.js';
import '../models/cart/cart-state.js';
import { CheckoutStepErrorType } from '../models/checkout/checkout-step-error-type.js';
import '../models/checkout-settings.js';
import '../models/step-shipping/shipping-methods.js';
import '../models/shared.js';
import 'history';
import { useGetQueryParams } from '../utils/get-query-params.js';
import '../utils/has-cart-errors.js';
import { getSelectedDonation } from '../selectors.js';
import '@grandvisionhq/graphql';
import '@grandvisionhq/svc-tealium/events';
import '../operations/mutations/set-customer-details.js';
import '../operations/mutations/set-service-store.js';
import '../operations/mutations/set-shipping-method.js';
import '../operations/mutations/set-shipping-state.js';
import '../operations/mutations/create-apple-pay-session.js';
import '../operations/mutations/recreate-ordered-checkout.js';
import { useCreateOrder } from '../hooks/orders/use-create-order.js';
import { useCreateOrderDraft } from '../hooks/orders/use-create-order-draft.js';
import { useRedirectToOrderComplete } from '../hooks/orders/use-redirect-to-order-confirm-page.js';
import '../operations/fragments/checkout-mutation-result.js';
import '../operations/fragments/cart-mutation-failures.js';
import '../operations/fragments/recreate-ordered-checkout-failure.js';
import '../operations/fragments/recreate-ordered-checkout-result.js';
import '../operations/fragments/paypal-payment.js';
import '../operations/fragments/saved-user-details.js';
import '../operations/queries/get-checkout-state.js';
import '../operations/queries/get-user-details.js';
import { useApplePay } from '../modules/ExpressCheckout/hooks/apple-pay/use-apple-pay.js';
import '../modules/ExpressCheckout/hooks/google-pay/use-google-pay.js';
import { selectPaymentMethod } from './actions.js';
import { useGetLabels } from './hooks/use-get-labels.js';
import { useHandleQueryParams } from './hooks/use-handle-query-params.js';
import { useHandleQrFormUpdates } from './hooks/use-handle-qr-form-updates.js';
import { StepPayment } from './StepPayment.js';
import { getBrowserInfo } from './utils/get-browser-info.js';

const StepPaymentContainer = ({ onPrevStep , selfUrl: selfUrl1 , updateCheckoutStatus , onNextStep ,  })=>{
    const { status , cartId , type: paymentType  } = useGetQueryParams();
    const [labels] = useGetLabels();
    const redirectToOrderComplete = useRedirectToOrderComplete();
    const [selectedPaymentMethod, setSelectedPaymentMethod] = React.useState();
    const [payCartWithApplePay] = useApplePay({
        isExpressPayment: false
    });
    const isApplePayActive = Hooks.useIsApplePayActive();
    // we need the recover session logic from this hook after a failed payment
    const authState = useAccount();
    const createOrderDraft = useCreateOrderDraft();
    const [createOrder, { orderResponse , error: createOrderError , paymentFlow , setPaymentFlow  }] = useCreateOrder();
    const [isLoading, setIsLoading] = React.useState(false);
    const [updateUrlQueryParam, deleteQueryParam] = useHandleQueryParams();
    const { href: homeHref  } = useLink({
        route: 'home'
    }, {
        replace: true
    }) || {};
    const { onClick: navigateToCart  } = useLink({
        route: 'cart.overview'
    }, {
        replace: true
    }) || {};
    const broadCastChannel = useSync('new-cart-created').getChannel();
    broadCastChannel.onmessage = ()=>{
        navigateToCart === null || navigateToCart === void 0 ? void 0 : navigateToCart();
    };
    const { replace  } = useLocation();
    const { cart , refetch  } = Context.useCart();
    const [_, { reset  }] = useCartStorage();
    const { isQrCodeModalOpen , setCurrentCardBrand , googlePayToken: googlePayToken1  } = usePaymentsUIContext();
    const prevIsQrCodeModalOpenValue = Util.usePrevious(isQrCodeModalOpen);
    const dispatch = useDispatch();
    const selectedDonation = useSelector(getSelectedDonation);
    React.useEffect(()=>{
        if (paymentType === PaymentMethod.APPLE_PAY && !isApplePayActive) {
            // this could happen if somebody is accessing the page directly in an unsupported browser
            toast.failed(ApplePaySessionErrors.ApplePaySessionUnsupported);
            deleteQueryParam([
                'type'
            ]);
        }
    }, [
        deleteQueryParam,
        isApplePayActive,
        paymentType,
        updateUrlQueryParam
    ]);
    React.useEffect(()=>{
        // Refetch cart after price update so that the latest price is reflected in the overview
        if (status === CheckoutStepErrorType.ProductPriceUpdated) {
            refetch();
        }
    }, [
        status,
        refetch
    ]);
    React.useEffect(()=>{
        if ((cart === null || cart === void 0 ? void 0 : cart.checkoutState) == 'Finalized') {
            reset();
            homeHref && replace(homeHref);
        }
    }, [
        cart === null || cart === void 0 ? void 0 : cart.checkoutState,
        homeHref,
        replace,
        reset
    ]);
    React.useEffect(()=>{
        if (createOrderError) {
            const checkoutStatus = createOrderError === CreateOrderErrors.PRODUCT_PRICE_UPDATED ? CheckoutStepErrorType.ProductPriceUpdated : CheckoutStepErrorType.Failed;
            updateCheckoutStatus && updateCheckoutStatus(checkoutStatus);
        }
    }, [
        createOrderError,
        updateCheckoutStatus
    ]);
    React.useEffect(()=>{
        /**
     *  Reset Payment flow when cartId changes, when QrCodeModal is being closed
     */ if (prevIsQrCodeModalOpenValue !== isQrCodeModalOpen) {
            if (cartId && !isQrCodeModalOpen) {
                setPaymentFlow(PaymentFlow.UNDEFINED);
            }
        }
    }, [
        cartId,
        isQrCodeModalOpen,
        prevIsQrCodeModalOpenValue,
        setPaymentFlow
    ]);
    React.useEffect(()=>{
        // Handle redirect for failed payment status
        if (status && (status === PaymentTypes.PaymentResult.Cancelled || status === PaymentTypes.PaymentResult.Refused || status === PaymentTypes.PaymentResult.Error || status === PaymentTypes.PaymentResult.InternalError)) {
            updateCheckoutStatus && updateCheckoutStatus(CheckoutStepErrorType.Failed);
        }
    // This code works as default constructor and need to be run only once when page/component loads
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [
        status,
        authState
    ]);
    const { paymentMethodLogos  } = useCheckoutSettings();
    /**
   * @description
   *  Callback method to provide details of selected payment method
   * @param paymentMethodConfig
   *  config of selected Payment method
   */ const handleOnPaymentMethodSelected = React.useCallback((paymentMethodConfig)=>{
        setSelectedPaymentMethod(paymentMethodConfig);
        //Update payment method in state
        dispatch(selectPaymentMethod({
            type: paymentMethodConfig.type,
            provider: paymentMethodConfig.provider
        }));
        setCurrentCardBrand(undefined);
        updateUrlQueryParam({
            type: paymentMethodConfig.type
        });
        /**
       *  Condition to change error status when the user tries to
       *  complete payment using different method
       */ if ((selectedPaymentMethod === null || selectedPaymentMethod === void 0 ? void 0 : selectedPaymentMethod.type) && (selectedPaymentMethod === null || selectedPaymentMethod === void 0 ? void 0 : selectedPaymentMethod.type) !== paymentMethodConfig.type && status !== '') {
            updateCheckoutStatus && updateCheckoutStatus('');
            deleteQueryParam([
                'status'
            ]);
        }
    }, [
        deleteQueryParam,
        dispatch,
        selectedPaymentMethod === null || selectedPaymentMethod === void 0 ? void 0 : selectedPaymentMethod.type,
        setCurrentCardBrand,
        status,
        updateCheckoutStatus,
        updateUrlQueryParam, 
    ]);
    /**
   * @description
   *  Handler to convert existing cart into order
   */ const handleCreateOrder = React.useCallback(function() {
        var _ref = swcHelpers.asyncToGenerator(function*(selfUrl, paymentDetails) {
            if (typeof cartId === 'string' && selectedPaymentMethod) {
                setIsLoading(true);
                const orderPayload = yield createOrderDraft({
                    paymentDetails,
                    selectedPaymentMethod,
                    returnUrl: `${selfUrl}/api/payment-callback?type=${selectedPaymentMethod.type}`,
                    overwritePersonalDetails: true
                });
                const createOrderResult = yield createOrder(orderPayload);
                if (createOrderResult && createOrderResult.success && !createOrderResult.redirect && createOrderResult.resultType !== 'QRCODE') {
                    try {
                        redirectToOrderComplete({
                            cartId: createOrderResult === null || createOrderResult === void 0 ? void 0 : createOrderResult.cartId,
                            orderData: createOrderResult === null || createOrderResult === void 0 ? void 0 : createOrderResult.orderDataToken
                        });
                    } catch (error) {
                        console.error(error);
                        setIsLoading(false);
                    }
                } else {
                    setIsLoading(false);
                }
            }
        });
        return function(selfUrl, paymentDetails) {
            return _ref.apply(this, arguments);
        };
    }(), [
        cartId,
        createOrder,
        createOrderDraft,
        redirectToOrderComplete,
        selectedPaymentMethod
    ]);
    /**
   * @description
   *  Callback method to store donation in the Redux store
   * @param donation
   *  donation amount
   */ const handleDonation = React.useCallback((donationPayload)=>{
        pushDonationSelectEventToDataLayer(donationPayload.foundationKey, donationPayload.amount);
        var _amount;
        dispatch(setDonation({
            paymentMethod: donationPayload.paymentMethod,
            amount: (_amount = donationPayload.amount) !== null && _amount !== void 0 ? _amount : null,
            key: donationPayload.foundationKey
        }));
    }, [
        dispatch
    ]);
    const createGooglePayOrder = React.useCallback((googlePayToken)=>{
        handleCreateOrder(selfUrl1, {
            isValid: true,
            type: PaymentMethod.GOOGLE_PAY,
            data: {
                googlePayToken,
                browserInfo: swcHelpers.objectSpread({}, getBrowserInfo())
            }
        });
    }, [
        handleCreateOrder,
        selfUrl1
    ]);
    React.useEffect(()=>{
        if (googlePayToken1) {
            createGooglePayOrder(googlePayToken1);
        }
    // to avoid unnecessary re-render removed createGooglePayOrder
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [
        googlePayToken1
    ]);
    const onPaypalError = ()=>{
        toast.failed('checkout.errors.general.message');
    };
    const createPaypalOrder = React.useCallback((paypalOrderId)=>{
        handleCreateOrder(selfUrl1, {
            isValid: true,
            type: PaymentMethod.PAYPAL,
            data: {
                referenceId: paypalOrderId
            }
        });
    }, [
        handleCreateOrder,
        selfUrl1
    ]);
    const paypalPaymentEvents = Hooks.useCreatePaypalPaymentEvents({
        hasSubscription: Boolean(cart === null || cart === void 0 ? void 0 : cart.hasSubscription),
        cartId: typeof cartId === 'string' ? cartId : '',
        onPaymentAuthorization: createPaypalOrder
    });
    const isPaymentStepValid = React.useCallback((paymentDetails)=>{
        const isValid = paymentDetails && paymentDetails.isValid;
        if (!isValid) {
            toast.failed('checkout.errors.form.payment.details');
        }
        return Boolean(isValid);
    }, []);
    const handleNextClick = React.useCallback(({ paymentDetailsConfig  } = {})=>{
        if (isPaymentStepValid(paymentDetailsConfig)) {
            updateCheckoutStatus && updateCheckoutStatus('');
            if ((selectedPaymentMethod === null || selectedPaymentMethod === void 0 ? void 0 : selectedPaymentMethod.type) === PaymentMethod.APPLE_PAY) {
                payCartWithApplePay === null || payCartWithApplePay === void 0 ? void 0 : payCartWithApplePay();
            } else {
                paymentDetailsConfig ? handleCreateOrder(selfUrl1, paymentDetailsConfig) : handleCreateOrder(selfUrl1);
            }
        }
    }, [
        isPaymentStepValid,
        selectedPaymentMethod === null || selectedPaymentMethod === void 0 ? void 0 : selectedPaymentMethod.type,
        updateCheckoutStatus,
        payCartWithApplePay,
        handleCreateOrder,
        selfUrl1, 
    ]);
    const handlePreviousClick = React.useCallback(()=>{
        onPrevStep && onPrevStep();
    }, [
        onPrevStep
    ]);
    var ref;
    const paymentMethodType = (ref = selectedPaymentMethod === null || selectedPaymentMethod === void 0 ? void 0 : selectedPaymentMethod.type) !== null && ref !== void 0 ? ref : 'swish';
    /**
   * @description
   * qrCodeFormData is needed for the new paymentMethodsUI and
   * useHandleQrFormUpdates hook also handles the QrForm state changes
   */ const [qrCodeFormData] = useHandleQrFormUpdates(swcHelpers.objectSpread({}, orderResponse ? {
        qrPaymentConfig: {
            qrCodeExpiresIn: orderResponse.qrCodeExpiresIn,
            qrCodeUrl: orderResponse.qrCodeUrl,
            redirectUrl: orderResponse.redirectUrl
        }
    } : {}, {
        paymentFlow,
        selfUrl: selfUrl1,
        paymentMethodType,
        paymentMethodLogos,
        orderId: paymentFlow === PaymentFlow.QR_CODE ? orderResponse === null || orderResponse === void 0 ? void 0 : orderResponse.orderId : undefined,
        orderDataToken: orderResponse === null || orderResponse === void 0 ? void 0 : orderResponse.orderDataToken
    }));
    /* onHandlePayment */ const onHandlePayment = React.useCallback((selectedPaymentConfig)=>{
        selectedPaymentConfig && updateUrlQueryParam({
            type: selectedPaymentConfig.type
        });
        onNextStep();
        handleNextClick({
            paymentDetailsConfig: selectedPaymentConfig
        });
    }, [
        updateUrlQueryParam,
        onNextStep,
        handleNextClick
    ]);
    var ref1;
    return /*#__PURE__*/ React.createElement(PaypalPaymentEventsProvider, swcHelpers.extends({}, paypalPaymentEvents, {
        onClick: onNextStep,
        orderType: (cart === null || cart === void 0 ? void 0 : cart.hasSubscription) ? 'subscription' : 'regular',
        onError: onPaypalError
    }), /*#__PURE__*/ React.createElement(StepPayment, {
        onNextClick: handleNextClick,
        onPrevClick: handlePreviousClick,
        onHandlePayment: onHandlePayment,
        qrCodeForm: qrCodeFormData,
        selectedPaymentMethod: (ref1 = paymentType) !== null && ref1 !== void 0 ? ref1 : '',
        onPaymentMethodSelected: handleOnPaymentMethodSelected,
        selectedDonation: selectedDonation === null || selectedDonation === void 0 ? void 0 : selectedDonation.amount,
        onDonationSelected: handleDonation,
        cartId: cartId,
        labels: labels,
        isOrderInProgress: isLoading,
        totalPrice: cart === null || cart === void 0 ? void 0 : cart.prices.totalPrice
    }));
};
const StepPaymentContainerWithUserData = WithUserData(StepPaymentContainer);

export { StepPaymentContainerWithUserData as default };
//# sourceMappingURL=StepPaymentContainer.js.map
